import type { EditorView } from '@codemirror/view';
import { atom, computed, map } from 'nanostores';
import type { FileMap, FilesStore } from './files';
import type { EditorDocument, ScrollPosition } from '~/components/editor/codemirror/CodeMirrorEditor';
import type { PartialFileContent } from '~/lib/runtime/message-parser';
import { WorkbenchStore } from '~/lib/stores/workbench';
import { hmrPropertyFactory } from '~/utils/hmr';
import { withResolvers } from '~/utils/promises';

const hmr = hmrPropertyFactory('EditorStore');

export type EditorDocuments = Record<string, EditorDocument>;

export class EditorStore {
  #filesStore: FilesStore;
  #hasUserSelectedFile = false;

  @hmr()
  selectedFile = atom<string | undefined>();

  @hmr()
  editorView = atom<EditorView | null>(null);

  @hmr()
  editorViewReady?: PromiseWithResolvers<void>;

  @hmr()
  streamingFile = atom<PartialFileContent | undefined>(undefined);

  @hmr()
  documents = map<EditorDocuments>({});

  currentDocument = computed(
    [this.documents, this.selectedFile, this.streamingFile],
    (documents, selectedFile, streamingFile) => {
      if (!selectedFile) {
        return undefined;
      }

      if (selectedFile === streamingFile?.path) {
        return {
          ...documents[selectedFile],
          value: streamingFile.content,
          filePath: streamingFile.path,
        } satisfies EditorDocument;
      }

      return documents[selectedFile];
    },
  );

  constructor(
    filesStore: FilesStore,
    private _workbenchStore: WorkbenchStore,
  ) {
    this.#filesStore = filesStore;

    const disposeStreamingSubscription = _workbenchStore.isStreaming.subscribe((isStreaming) => {
      if (isStreaming) {
        this.#hasUserSelectedFile = false;
      }
    });

    if (import.meta.hot) {
      import.meta.hot.data.disposeStreamingSubscription?.();
      import.meta.hot.data.disposeStreamingSubscription = disposeStreamingSubscription;
    }
  }

  setStreamingFile(data: PartialFileContent) {
    if (data.status === 'start') {
      if (!this.#hasUserSelectedFile) {
        this.setSelectedFileProgrammatically(data.path);
      }

      this.#filesStore.createInMemoryFileIfMissing(data.path);

      this.streamingFile.set(data);
    } else if (data.status === 'stream') {
      this.streamingFile.set(data);
    } else if (data.status === 'end') {
      this.streamingFile.set(undefined);
    }
  }

  setDocuments(files: FileMap) {
    const previousDocuments = this.documents.value;
    const nextDocuments: { [key: string]: EditorDocument } = {};

    for (const filePath in files) {
      const dirent = files[filePath];

      if (dirent && dirent.type !== 'folder') {
        const previousDocument = previousDocuments?.[filePath];

        nextDocuments[filePath] = {
          value: dirent.content,
          filePath,
          scroll: previousDocument?.scroll,
          isBinary: dirent.isBinary || Boolean(dirent.blobId),
        } as EditorDocument;
      }
    }

    this.documents.set(nextDocuments);
  }

  setSelectedFile(filePath: string | undefined) {
    this.setSelectedFileProgrammatically(filePath);
    this.#hasUserSelectedFile = true;
  }

  setSelectedFileProgrammatically(filePath: string | undefined) {
    if (this.selectedFile.get() !== filePath) {
      this.editorViewReady = withResolvers<void>();
    }

    this.selectedFile.set(filePath);
  }

  setEditorViewReady() {
    this.editorViewReady?.resolve?.();
  }

  updateScrollPosition(filePath: string, position: ScrollPosition) {
    const documents = this.documents.get();
    const documentState = documents[filePath];

    if (!documentState) {
      return;
    }

    this.documents.setKey(filePath, {
      ...documentState,
      scroll: position,
    });
  }

  updateFile(filePath: string, newContent: string) {
    const documents = this.documents.get();
    const documentState = documents[filePath];

    if (!documentState) {
      return;
    }

    const currentContent = documentState.value;
    const contentChanged = currentContent !== newContent;

    if (contentChanged) {
      this.documents.setKey(filePath, {
        ...documentState,
        value: newContent,
      });
    }
  }
}
